import {
  PurchasedGoodsInsightsRetrieveParams,
  PurchasedGoodsItemsListParams,
  PurchasedGoodsTaxonomyListParams,
} from '@shared/api/types';
import { UNCATEGORISED_SLUG } from '@app/purchased-goods/components/taxonomy-filter-nodes';
import {
  parseAsArrayOf,
  parseAsInteger,
  parseAsString,
  parseAsStringLiteral,
  useQueryState,
  useQueryStates,
} from 'nuqs';
import { useCallback, useMemo } from 'react';

/**
 * URL-backed period selection — TimePeriod boundary uuids, matching the API's
 * params directly. Absent params mean "no explicit range": the backend applies
 * its default window (the most recent reporting year) and echoes it in the
 * response for the picker to display.
 */
export function useInsightsPeriodRange() {
  return useQueryStates({
    periodStart: parseAsString,
    periodEnd: parseAsString,
  });
}

/**
 * Chart types across both states: `emissions`/`spend` are time series (overall
 * line, grouped stacked area); `scatter`/`intensity` are the grouped scatter
 * views.
 */
export const INSIGHTS_CHART_TYPES = [
  'emissions',
  'spend',
  'scatter',
  'intensity',
] as const;
export type InsightsChartType = (typeof INSIGHTS_CHART_TYPES)[number];

/** URL-backed active chart type (`?chart=`), defaulting to emissions. */
export function useInsightsChartType() {
  return useQueryState(
    'chart',
    parseAsStringLiteral(INSIGHTS_CHART_TYPES).withDefault('emissions')
  );
}

/**
 * What the user picked in the Explore row. `category` covers all three
 * taxonomy tiers at once — which one is shown follows from how deep the
 * taxonomy filter is set (see `useInsightsDimension`).
 */
export const INSIGHTS_EXPLORE = [
  'overall',
  'site',
  'supplier',
  'category',
  'item',
] as const;
export type InsightsExplore = (typeof INSIGHTS_EXPLORE)[number];

/**
 * `food` was the base-food grouping before the product taxonomy replaced it;
 * links from that era land on the taxonomy view rather than falling back to
 * Overall.
 */
const EXPLORE_PARAM_VALUES = [...INSIGHTS_EXPLORE, 'food'] as const;

/**
 * URL-backed active grouping (`?explore=`), defaulting to Overall. Changing it
 * is a move through the data rather than a refinement of it, so it pushes a
 * history entry and the back button returns to the previous level.
 */
export function useInsightsExplore(): [
  InsightsExplore,
  (value: InsightsExplore | null) => void,
] {
  const [raw, setRaw] = useQueryState(
    'explore',
    parseAsStringLiteral(EXPLORE_PARAM_VALUES)
      .withDefault('overall')
      .withOptions({ history: 'push' })
  );

  const setExplore = useCallback(
    (value: InsightsExplore | null) => setRaw(value),
    [setRaw]
  );

  return [raw === 'food' ? 'category' : raw, setExplore];
}

/** The taxonomy tiers, outermost first. Order is the drill order. */
export const INSIGHTS_TAXONOMY_TIERS = [
  'productCategory',
  'productSubCategory',
  'productBase',
] as const;
export type InsightsTaxonomyTier = (typeof INSIGHTS_TAXONOMY_TIERS)[number];

/**
 * Whether a taxonomy path has no tier left to break down: either it reaches a
 * base product, or it lands on the uncategorised bucket, which reads the same
 * at every tier and so has nothing distinct beneath it.
 */
export const isTerminalTaxonomyPath = (path: readonly string[]) =>
  path.length === INSIGHTS_TAXONOMY_TIERS.length ||
  path.includes(UNCATEGORISED_SLUG);

const EMPTY_TAXONOMY = {
  productCategory: null,
  productSubCategory: null,
  productBase: null,
};

/**
 * URL-backed taxonomy selection — one slug per tier, forming a single path
 * down the tree. Selecting at one tier clears the tiers below it, so the
 * selection is always a contiguous path from the top.
 *
 * Each step down pushes a history entry, so the back button walks back up the
 * path one tier at a time.
 */
export function useInsightsTaxonomy() {
  const [values, setValues] = useQueryStates(
    {
      productCategory: parseAsString,
      productSubCategory: parseAsString,
      productBase: parseAsString,
    },
    { history: 'push' }
  );

  /** How many tiers deep the selection goes, judged by the deepest one set. */
  const depth = useMemo(() => {
    const deepest = INSIGHTS_TAXONOMY_TIERS.reduce(
      (found, tier, index) => (values[tier] ? index : found),
      -1
    );
    return deepest + 1;
  }, [values]);

  const select = useCallback(
    (tier: InsightsTaxonomyTier, slug: string | null) => {
      const level = INSIGHTS_TAXONOMY_TIERS.indexOf(tier);
      // Tiers above the one selected are kept; the selected one takes the new
      // slug and everything below it is dropped.
      const valueFor = (candidate: InsightsTaxonomyTier) => {
        const candidateLevel = INSIGHTS_TAXONOMY_TIERS.indexOf(candidate);
        if (candidateLevel < level) return values[candidate];
        return candidateLevel === level ? slug : null;
      };
      setValues({
        productCategory: valueFor('productCategory'),
        productSubCategory: valueFor('productSubCategory'),
        productBase: valueFor('productBase'),
      });
    },
    [setValues, values]
  );

  /** Set the whole path at once — what the cascading filter hands back. */
  const selectPath = useCallback(
    (path: readonly string[]) =>
      setValues({
        productCategory: path[0] ?? null,
        productSubCategory: path[1] ?? null,
        productBase: path[2] ?? null,
      }),
    [setValues]
  );

  const clear = useCallback(() => setValues(EMPTY_TAXONOMY), [setValues]);

  /** The selection as a path, outermost first, stopping at the first gap. */
  const path = useMemo(() => {
    const selected: string[] = [];
    for (const tier of INSIGHTS_TAXONOMY_TIERS) {
      if (!values[tier]) break;
      selected.push(values[tier] as string);
    }
    return selected;
  }, [values]);

  return { values, path, depth, select, selectPath, clear };
}

/**
 * The dimension actually being broken down — the API's `groupBy`. It is the
 * user's Explore choice, except for `category`, where the taxonomy filter's
 * depth decides which tier is listed: the one below the deepest selection, so
 * every selection opens up the level beneath it, and a fully specified path
 * lands on the items themselves.
 */
export const INSIGHTS_DIMENSIONS = [
  'overall',
  'site',
  'supplier',
  'productCategory',
  'productSubCategory',
  'productBase',
  'item',
] as const;
export type InsightsDimension = (typeof INSIGHTS_DIMENSIONS)[number];

const TAXONOMY_DIMENSION_BY_DEPTH: InsightsDimension[] = [
  'productCategory',
  'productSubCategory',
  'productBase',
];

export function useInsightsDimension(): InsightsDimension {
  const [explore] = useInsightsExplore();
  const { path, depth } = useInsightsTaxonomy();

  if (explore !== 'category') return explore;
  // A terminal path has no tier left to break down; the goods under it are the
  // Item view, which selecting a base product or the uncategorised bucket
  // moves to. Should the user come back to By category, the tier that holds
  // the selection is listed rather than an identical bucket beneath it.
  const shown = isTerminalTaxonomyPath(path) ? depth - 1 : depth;
  return TAXONOMY_DIMENSION_BY_DEPTH[
    Math.min(shown, TAXONOMY_DIMENSION_BY_DEPTH.length - 1)
  ];
}

/**
 * URL-backed filter values for the dimensions that are plain multi-select
 * lists of uuids. The taxonomy lives in `useInsightsTaxonomy` instead — it is
 * a path, not a set.
 */
export function useInsightsFilters() {
  return useQueryStates({
    site: parseAsArrayOf(parseAsString).withDefault([]),
    supplier: parseAsArrayOf(parseAsString).withDefault([]),
    item: parseAsArrayOf(parseAsString).withDefault([]),
  });
}

/** URL-backed breakdown-table state: page (1-based), page size, and sort. */
export function useInsightsTableState() {
  return useQueryStates({
    page: parseAsInteger.withDefault(1),
    pageSize: parseAsInteger.withDefault(10),
    sort: parseAsString,
  });
}

/** The taxonomy selection as request params — one slug per set tier. */
function taxonomyParams(
  values: ReturnType<typeof useInsightsTaxonomy>['values']
) {
  return {
    ...(values.productCategory
      ? { productCategory: [values.productCategory] }
      : {}),
    ...(values.productSubCategory
      ? { productSubCategory: [values.productSubCategory] }
      : {}),
    ...(values.productBase ? { productBase: [values.productBase] } : {}),
  };
}

/**
 * Assembles the full insights request params from the page's URL state:
 * grouping, filters, period boundaries, and table pagination/sort.
 */
export function useInsightsApiParams(): PurchasedGoodsInsightsRetrieveParams {
  const dimension = useInsightsDimension();
  const [filters] = useInsightsFilters();
  const { values: taxonomy } = useInsightsTaxonomy();
  const [range] = useInsightsPeriodRange();
  const [tableState] = useInsightsTableState();

  return useMemo(() => {
    const params: PurchasedGoodsInsightsRetrieveParams = {};
    if (range.periodStart && range.periodEnd) {
      params.timePeriodStartUuid = range.periodStart;
      params.timePeriodEndUuid = range.periodEnd;
    }
    if (dimension !== 'overall') {
      params.groupBy = dimension;
      params.page = tableState.page;
      params.page_size = tableState.pageSize;
      if (tableState.sort) {
        params.sort = [
          tableState.sort,
        ] as PurchasedGoodsInsightsRetrieveParams['sort'];
      }
    }
    if (filters.site.length) params.site = filters.site;
    if (filters.supplier.length) params.supplier = filters.supplier;
    if (filters.item.length) params.item = filters.item;
    return { ...params, ...taxonomyParams(taxonomy) };
  }, [dimension, filters, taxonomy, range, tableState]);
}

/**
 * The active scope as item-search params: every dimension filter and the
 * period window, excluding the item dimension itself (a facet never restricts
 * by its own filter). Mirrors the insights request, so the picker offers only
 * items that exist within the current view.
 */
export function useInsightsItemSearchScope(): Omit<
  PurchasedGoodsItemsListParams,
  'item' | 'search'
> {
  const [filters] = useInsightsFilters();
  const { values: taxonomy } = useInsightsTaxonomy();
  const [range] = useInsightsPeriodRange();

  return useMemo(() => {
    const scope: Omit<PurchasedGoodsItemsListParams, 'item' | 'search'> = {};
    if (range.periodStart && range.periodEnd) {
      scope.timePeriodStartUuid = range.periodStart;
      scope.timePeriodEndUuid = range.periodEnd;
    }
    if (filters.site.length) scope.site = filters.site;
    if (filters.supplier.length) scope.supplier = filters.supplier;
    return { ...scope, ...taxonomyParams(taxonomy) };
  }, [filters, taxonomy, range]);
}

/**
 * The scope the taxonomy tree is offered under: the dimensions that decide
 * which products the organisation actually bought, and the period.
 *
 * Deliberately excludes the taxonomy selection itself. The endpoint ignores it
 * anyway, and leaving it out keeps one cached tree for the whole drill, so
 * moving down the columns costs no request.
 */
export function useInsightsTaxonomyScope(): PurchasedGoodsTaxonomyListParams {
  const [filters] = useInsightsFilters();
  const [range] = useInsightsPeriodRange();

  return useMemo(() => {
    const scope: PurchasedGoodsTaxonomyListParams = {};
    if (range.periodStart && range.periodEnd) {
      scope.timePeriodStartUuid = range.periodStart;
      scope.timePeriodEndUuid = range.periodEnd;
    }
    if (filters.site.length) scope.site = filters.site;
    if (filters.supplier.length) scope.supplier = filters.supplier;
    if (filters.item.length) scope.item = filters.item;
    return scope;
  }, [filters, range]);
}
